import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { dataCurta, dataLonga, diasAte, mesLongo, urgenciaDoPrazo } from "../lib/concursos";
import type { Concurso } from "../lib/mapaConcursos";

// As peças do Mapa de Concursos que não são a lista: o alerta de prazo, a
// navegação entre as duas visões e a agenda de provas por mês.
//
// ===========================================================================
// O link do grupo mora AQUI, e numa constante só
// ===========================================================================
// No app de origem ele está CRAVADO em quatro arquivos — `Header.tsx:220`,
// `BottomNav.tsx:4`, `FirstVisitPopup.tsx:35` e `WhatsAppButton.tsx:3` — e dois
// deles são componentes órfãos que nada importa. Quatro cópias do mesmo endereço
// é a receita para três ficarem velhas.
//
// E não é `wa.me` nem `chat.whatsapp.com`: é um encurtador da própria casa, que
// redireciona. Isso importa porque a regra de `cards.url` do hub exige domínio da
// casa em endereço que vira `href` — este satisfaz.
export const GRUPO_DE_VAGAS = "https://ec.cfaci.me/mapa-concursos-grupo";

/**
 * O alerta dos favoritos que estão fechando.
 *
 * É a razão de existir da ferramenta: o aluno marca o que quer acompanhar e o
 * prazo o encontra. A regra é a da origem — favoritos com inscrição ABERTA cujo
 * fim cai entre hoje e 15 dias, do mais urgente para o menos.
 *
 * ---------------------------------------------------------------------------
 * Três coisas mudaram, e as três por medição
 * ---------------------------------------------------------------------------
 * **1. O prazo vem da COLUNA, não de um regex sobre texto livre.** Na origem
 * este componente tem a sua própria cópia da regra — `/a\s+(?:\d{1,2}h\s+)?
 * (\d{2})\/(\d{2})\/(\d{4})/`, a primeira ocorrência, à meia-noite — e ela é a
 * TERCEIRA implementação divergente de "quando encerra" no mesmo projeto.
 * Medido: em `"de 20/01/2026 ate 15/02/2026"` ela não casa nada e o aluno não é
 * avisado; em `"Reabertas para 20/02/2026"` ela casa o "a" de "para" e avisa de
 * um prazo que não existe. Aqui a data é `inscricao_fim`.
 *
 * **2. O X LEMBRA.** Na origem o dispensar é `useState`, então recarregar traz o
 * alerta de volta — "botão que não lembra ensina que a tela não escuta". Aqui
 * ele vale pelo DIA e por aluno, no `localStorage`: amanhã o aviso volta, que é
 * o certo para um prazo que anda.
 *
 * **3. A urgência é dita com LETRA, não só com cor.** "encerra em 3 dias" é o
 * texto; a cor acompanha. Cor sozinha não informa quem não distingue vermelho de
 * laranja — e é o mesmo motivo de as dez notas do formulário de feedback da
 * origem terem ficado de fora (as dez reprovam contraste com o texto branco por
 * cima, oito delas abaixo de 3:1).
 */
export function AlertaDePrazo({
  concursos,
  hoje,
  alunoId,
  onAbrir,
}: {
  concursos: Concurso[];
  hoje: string;
  /** Para a dispensa ser por aluno: dois alunos no mesmo navegador não herdam. */
  alunoId: string;
  onAbrir: (c: Concurso) => void;
}) {
  const chave = `mc-alerta-dispensado:${alunoId}:${hoje}`;
  const [dispensado, setDispensado] = useState(false);

  useEffect(() => {
    // `localStorage` pode estourar (aba privada, storage bloqueado). Se estourar,
    // o alerta APARECE — errar para mais é reversível; deixar de avisar de um
    // prazo, não.
    try {
      setDispensado(window.localStorage.getItem(chave) === "1");
    } catch {
      setDispensado(false);
    }
  }, [chave]);

  const urgentes = concursos
    .filter((c) => c.favorito && c.situacao === "aberta" && c.janela)
    .map((c) => ({ c, dias: diasAte(hoje, c.janela!.fim) }))
    .filter(({ dias }) => dias >= 0 && dias <= 15)
    .sort((a, b) => a.dias - b.dias || a.c.orgao.localeCompare(b.c.orgao, "pt-BR"));

  if (dispensado || urgentes.length === 0) return null;

  return (
    <section
      role="group"
      aria-labelledby="titulo-alerta-prazo"
      data-alerta-prazo
      className="relative mt-6 rounded-cf border border-alerta-tinta/30 bg-alerta-fundo p-4 sm:p-5"
    >
      <button
        type="button"
        onClick={() => {
          setDispensado(true);
          try {
            window.localStorage.setItem(chave, "1");
          } catch {
            /* sem storage, o alerta só some até recarregar — e tudo bem */
          }
        }}
        className="absolute top-3 right-3 text-alerta-tinta/70 hover:text-alerta-tinta"
      >
        <span aria-hidden="true" className="text-lg leading-none">
          ×
        </span>
        <span className="sr-only">Dispensar este aviso por hoje</span>
      </button>

      <h2
        id="titulo-alerta-prazo"
        className="pr-8 font-display text-base font-bold text-alerta-tinta"
      >
        {urgentes.length === 1
          ? "Um concurso que você acompanha está fechando"
          : `${urgentes.length} concursos que você acompanha estão fechando`}
      </h2>

      <ul className="mt-3 space-y-1.5">
        {urgentes.map(({ c, dias }) => {
          const u = urgenciaDoPrazo(dias);
          return (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => onAbrir(c)}
                data-urgente={c.id}
                className="flex w-full flex-wrap items-baseline gap-x-2 text-left text-sm hover:underline"
              >
                <span className="font-semibold text-alerta-tinta">{u?.texto}</span>
                <span className="text-tinta">
                  {c.orgao} · {c.cargo}
                </span>
                <span className="text-cinza">
                  até {dataLonga(c.janela!.fim)}
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      <p className="pt-3 text-xs leading-relaxed text-cinza">
        O prazo sai do que a equipe leu no edital. Confirme no site da banca antes de pagar
        a taxa — o aviso volta amanhã se você dispensar.
      </p>
    </section>
  );
}

/**
 * As duas visões da mesma lista, como SUB-ROTA.
 *
 * `/ferramenta/mapa-de-concursos` e `/ferramenta/mapa-de-concursos/provas`. Aba
 * de ferramenta é sub-rota e não `useState`: o aluno volta pelo botão do
 * navegador, compartilha o link de uma aba, e recarregar não o joga para a
 * primeira. É a mesma decisão do HUB DP.
 *
 * No app de origem são duas ROTAS de topo (`/` e `/calendario`), e o rótulo delas
 * DIVERGE entre os dois lugares que as apontam: o cabeçalho chama de
 * "Calendário" e a barra de baixo chama de "Provas". Aqui o rótulo está num lugar
 * só.
 */
export function AbasDoMapa({ provas }: { provas: number }) {
  const { pathname } = useLocation();
  const base = "/ferramenta/mapa-de-concursos";
  const emProvas = pathname.startsWith(`${base}/provas`);

  const classe = (ativo: boolean) =>
    `rounded-full px-4 py-1.5 text-sm font-semibold transition ${
      ativo ? "bg-laranja text-navy-ink" : "bg-white/10 text-white hover:bg-white/20"
    }`;

  return (
    <nav aria-label="Visões do mapa" className="flex flex-wrap gap-2">
      <Link to={base} className={classe(!emProvas)} aria-current={!emProvas ? "page" : undefined}>
        Lista de concursos
      </Link>
      <Link
        to={`${base}/provas`}
        className={classe(emProvas)}
        aria-current={emProvas ? "page" : undefined}
      >
        Agenda de provas <span className="tabular-nums opacity-80">{provas}</span>
      </Link>
    </nav>
  );
}

/**
 * A agenda de provas, agrupada por mês.
 *
 * ---------------------------------------------------------------------------
 * Por que é uma VISTA da mesma tabela, e não uma segunda lista
 * ---------------------------------------------------------------------------
 * O app de origem tem uma tabela separada (`calendario_provas`) alimentada por
 * uma segunda aba de planilha. Medido em 27/08/2026: 204 linhas, **todas com data
 * no passado** (a mais nova é 01/08/2026), 6 das 11 colunas 100% vazias, e só 46
 * dos 199 órgãos dela aparecem em `concursos` — as duas listas não se ligam. A
 * aba que a alimenta se chama literalmente "Calendário de provas 2025".
 *
 * Enquanto isso a data da prova já existe em cada concurso, com **81 datas
 * futuras** até 24/01/2027. Então aqui a agenda é o mesmo dado, agrupado — e ela
 * envelhece junto com a lista, em vez de morrer sozinha.
 *
 * A proximidade sai da MESMA função de urgência do alerta de prazo, para não
 * existirem duas escalas de "está perto" na mesma ferramenta.
 */
export function AgendaDeProvas({
  concursos,
  hoje,
  onAbrir,
}: {
  concursos: Concurso[];
  hoje: string;
  onAbrir: (c: Concurso) => void;
}) {
  const [mostrarPassadas, setMostrarPassadas] = useState(false);

  const comData = concursos.filter((c) => c.dataProva);
  const futuras = comData.filter((c) => c.dataProva! >= hoje);
  const passadas = comData.filter((c) => c.dataProva! < hoje);
  const semData = concursos.length - comData.length;

  const visiveis = mostrarPassadas ? comData : futuras;

  // Agrupa por aaaa-mm, na ordem do calendário.
  const meses = new Map<string, Concurso[]>();
  for (const c of [...visiveis].sort(
    (a, b) =>
      a.dataProva!.localeCompare(b.dataProva!) || a.orgao.localeCompare(b.orgao, "pt-BR"),
  )) {
    const k = c.dataProva!.slice(0, 7);
    if (!meses.has(k)) meses.set(k, []);
    meses.get(k)!.push(c);
  }

  return (
    <section role="group" aria-labelledby="titulo-agenda" className="mt-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 id="titulo-agenda" className="font-display text-xl font-bold text-titulo">
            {futuras.length} {futuras.length === 1 ? "prova marcada" : "provas marcadas"}
          </h2>
          <p className="pt-1 text-sm text-cinza">
            A data que a equipe leu no edital, agrupada por mês.
            {/* A AUSÊNCIA é dita. São os concursos cujo edital ainda não marcou
                data — esconder o número faria a agenda parecer o catálogo
                inteiro. */}
            {semData > 0 &&
              ` Outros ${semData} concursos ainda não têm data de prova no cadastro.`}
          </p>
        </div>
        {passadas.length > 0 && (
          <button
            type="button"
            onClick={() => setMostrarPassadas((v) => !v)}
            aria-pressed={mostrarPassadas}
            className="btn-contorno"
          >
            {mostrarPassadas
              ? "Esconder as já realizadas"
              : `Mostrar as ${passadas.length} já realizadas`}
          </button>
        )}
      </div>

      {meses.size === 0 ? (
        <p className="surface mt-4 p-6 text-sm text-cinza">
          Nenhuma prova marcada daqui para a frente. Quando a equipe cadastrar a data de um
          edital novo, ela aparece aqui.
        </p>
      ) : (
        <div className="mt-4 space-y-6">
          {[...meses.entries()].map(([mes, lista]) => (
            <div key={mes} data-mes={mes}>
              <h3 className="font-display text-base font-bold text-titulo capitalize">
                {mesLongo(mes)}{" "}
                <span className="text-sm font-semibold text-cinza tabular-nums">
                  {lista.length} {lista.length === 1 ? "prova" : "provas"}
                </span>
              </h3>
              <ul className="mt-2 space-y-2">
                {lista.map((c) => {
                  const dias = diasAte(hoje, c.dataProva!);
                  const u = dias >= 0 ? urgenciaDoPrazo(dias) : null;
                  return (
                    <li key={c.id}>
                      <button
                        type="button"
                        onClick={() => onAbrir(c)}
                        data-abrir={c.id}
                        className="surface flex w-full flex-col gap-1 p-4 text-left sm:flex-row sm:items-center sm:gap-4"
                      >
                        <span className="flex shrink-0 items-baseline gap-2 sm:w-40">
                          <span className="font-display text-lg font-bold tabular-nums text-titulo">
                            {dataCurta(c.dataProva)}
                          </span>
                          {dias < 0 ? (
                            <span className="rounded-full bg-chip px-2 py-0.5 text-xs font-semibold text-cinza">
                              realizada
                            </span>
                          ) : (
                            u && (
                              <span
                                className={`rounded-full px-2 py-0.5 text-xs font-bold ${
                                  u.nivel === "hoje" || u.nivel === "critico"
                                    ? "bg-erro-fundo text-erro-tinta"
                                    : u.nivel === "atencao"
                                      ? "bg-alerta-fundo text-alerta-tinta"
                                      : "bg-aviso-fundo text-aviso-tinta"
                                }`}
                              >
                                {/* O texto de `urgenciaDoPrazo` fala de
                                    encerramento; aqui o fato é a PROVA. Mesma
                                    escala, palavra certa. */}
                                {dias === 0 ? "é hoje" : dias === 1 ? "é amanhã" : `em ${dias} dias`}
                              </span>
                            )
                          )}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block font-semibold text-titulo">{c.orgao}</span>
                          <span className="block text-sm text-cinza">
                            {c.cargo} · <span data-uf={c.uf}>{c.uf}</span>
                            {c.localProva ? ` · ${c.localProva}` : c.cidade ? ` · ${c.cidade}` : ""}
                            {c.banca && ` · ${c.banca}`}
                          </span>
                        </span>
                        {c.favorito && (
                          <span className="shrink-0 rounded-full bg-tile px-2.5 py-1 text-xs font-bold text-laranja-texto">
                            você acompanha
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
